import {
  Avatar,
  Box,
  Button,
  Divider,
  Input,
  InputGroup,
  InputLeftElement,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
} from '@chakra-ui/react';
import { ChevronDown, UserPlus, Search, UserX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { t } from '@lingui/core/macro';
import type { RouterOutputs } from '@duke-hq/svc-api/admin';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { useAuthAdmin } from '../AuthProviderAdmin';

export type AdminUser = RouterOutputs['users']['all'][number];

type Props = {
  currentAssignee?: { id: string; first_name: string; last_name: string | null } | null;
  onAssign: (drn: string | null) => void;
  enabled?: boolean;
  selfAssignOnly?: boolean;
  // Pass the admin-user list down from the page when this dropdown is rendered per row,
  // so the list isn't fetched and processed once per row. Falls back to fetching when omitted.
  users?: AdminUser[];
};

export function AssignAdminDropdown({
  currentAssignee,
  onAssign,
  enabled = true,
  selfAssignOnly = false,
  users: usersProp,
}: Props) {
  const { data: fetchedUsers } = trpcAdmin.users.all.useQuery(
    {},
    { initialData: [], enabled: usersProp == null },
  );
  const users = usersProp ?? fetchedUsers;
  const auth = useAuthAdmin();

  const [searchQuery, setSearchQuery] = useState('');

  // Separate the logged in user from the other users
  const { loggedInUser, otherUsers } = useMemo(() => {
    const loggedInUserIndex = users.findIndex((user) => user.id === auth?.user?.id);
    if (loggedInUserIndex === -1) {
      return { otherUsers: users };
    }

    const otherUsers = [...users];
    const [loggedInUser] = otherUsers.splice(loggedInUserIndex, 1);
    return { loggedInUser, otherUsers };
  }, [users, auth.user]);

  // Filter the other users based on the search query
  const filteredUsers = useMemo(() => {
    return otherUsers.filter(
      ({ first_name, last_name }) =>
        searchQuery === '' ||
        `${first_name} ${last_name}`.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }, [otherUsers, searchQuery]);

  if (!enabled) {
    return (
      <Box display="flex" alignItems="center" px={1} py={1}>
        <Avatar
          size="sm"
          name={`${currentAssignee?.first_name} ${currentAssignee?.last_name}`}
          mr={2}
        />
        <Text fontSize="sm">
          {currentAssignee?.first_name} {currentAssignee?.last_name}
        </Text>
      </Box>
    );
  }

  return (
    <Box onClick={(e) => e.stopPropagation()}>
      <Menu isLazy>
        <MenuButton
          as={Button}
          variant="ghost"
          size="sm"
          rightIcon={<ChevronDown size="14px" />}
          px={1}
          py={1}
        >
          {currentAssignee ? (
            <Box display="flex" alignItems="center">
              <Avatar
                size="sm"
                name={`${currentAssignee.first_name} ${currentAssignee.last_name}`}
                mr={2}
              />
              <Text fontSize="sm">
                {currentAssignee.first_name} {currentAssignee.last_name}
              </Text>
            </Box>
          ) : (
            <Box display="flex" alignItems="center">
              <Avatar
                size="sm"
                mr={2}
                icon={<UserPlus size="16px" />}
                color="gray.500"
                borderColor="gray.200"
                borderWidth="2px"
                bg="transparent"
              />
              <Text fontSize="sm" color="gray.500">
                {t`Unassigned`}
              </Text>
            </Box>
          )}
        </MenuButton>
        <MenuList>
          {currentAssignee != null ? (
            <MenuItem fontSize="sm" onClick={() => onAssign(null)}>
              <Box display="flex" alignItems="center">
                <Avatar
                  size="xs"
                  mr={2}
                  icon={<UserX size="14px" />}
                  color="gray.500"
                  bg="transparent"
                  borderColor="gray.200"
                  borderWidth="1px"
                />
                <Text color="danger">{t`Unassign`}</Text>
              </Box>
            </MenuItem>
          ) : null}

          {loggedInUser !== undefined && (
            <MenuItem
              key={loggedInUser.id}
              fontSize="sm"
              onClick={() => onAssign(loggedInUser.drn)}
              bg={loggedInUser.id === currentAssignee?.id ? 'bg.subtle' : undefined}
              py={1}
            >
              <Avatar
                size="xs"
                name={`${loggedInUser.first_name} ${loggedInUser.last_name}`}
                mr={2}
              />
              <Text>
                {loggedInUser.first_name} {loggedInUser.last_name}
              </Text>
            </MenuItem>
          )}

          {!selfAssignOnly && (
            <>
              <Divider my={2} />

              <Box px={2} py={1} mb={1}>
                <InputGroup size="sm">
                  <InputLeftElement>
                    <Search size="14px" />
                  </InputLeftElement>
                  <Input
                    placeholder={t`Search...`}
                    type="search"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </InputGroup>
              </Box>
              <Box maxH="200px" overflowY="auto">
                {filteredUsers.map((user) => (
                  <MenuItem
                    key={user.id}
                    fontSize="sm"
                    onClick={() => onAssign(user.drn)}
                    bg={user.id === currentAssignee?.id ? 'bg.subtle' : undefined}
                    py={1}
                  >
                    <Avatar size="xs" name={`${user.first_name} ${user.last_name}`} mr={2} />
                    <Text>
                      {user.first_name} {user.last_name}
                    </Text>
                  </MenuItem>
                ))}
              </Box>
            </>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
}
